import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-500",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/70 focus:ring-destructive/50",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent/70 focus:ring-gray-400",
        secondary: "bg-gray-100 text-gray-800 hover:bg-gray-200 active:bg-gray-300 focus:ring-gray-400",
        ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-800 active:bg-gray-200 focus:ring-gray-400",
        link: "text-primary-600 underline-offset-4 hover:underline focus:ring-primary-500",
        tonal: "bg-primary-100 text-primary-800 hover:bg-primary-200 active:bg-primary-300 focus:ring-primary-400",
        outlineSecondary: "border border-gray-300 text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-400",
        outlinePrimary: "border border-primary-500 text-primary-600 hover:bg-primary-50 active:bg-primary-100 focus:ring-primary-400",
        subtle: "bg-gray-50 text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-300",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        md: "h-[36px] px-4",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
        custom: "h-[40px] px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }